import {FunctionComponent, ReactNode, useEffect, useState} from 'react';
import {AuthUser, fetchAuthSession, getCurrentUser, JWT} from 'aws-amplify/auth';
import {AuthContext, AuthStatus} from '../context/AuthContext';

interface IAuthProvider {
    children?: ReactNode;
}

// Subscribes to Amplify v6's session API and surfaces a {user, tokens,
// groups, role} snapshot via AuthContext. Apps typically extend this
// with sign-in / sign-out / federated-IdP flows. Mirrors the bot/portal/
// console pattern.
export const AuthProvider: FunctionComponent<IAuthProvider> = ({children}) => {
    const [user, setUser] = useState<AuthUser | undefined>();
    // FNP-1501: 'resolving' until getCurrentUser() answers. Without this the
    // guard cannot tell "still checking" from "not signed in" — see the note
    // on AuthStatus.
    const [status, setStatus] = useState<AuthStatus>('resolving');
    const [idToken, setIdToken] = useState<JWT | undefined>();
    const [accessToken, setAccessToken] = useState<JWT | undefined>();
    const [cognitoGroups, setCognitoGroups] = useState<string[] | undefined>();
    const [role, setRole] = useState<string | undefined>();

    useEffect(() => {
        (async () => {
            try {
                const result = await getCurrentUser();
                const {username, userId, signInDetails} = result;
                setUser({username, userId, signInDetails});
                setStatus('authenticated');
            } catch (error) {
                // ⚠️ THE COMMON CASE, NOT AN EXCEPTION. Amplify throws here
                // when nobody is signed in, so this branch is how a normal
                // anonymous visit resolves. Setting the status is what ends
                // the guard's wait; leaving it at 'resolving' would hang the
                // app on a spinner forever for every signed-out visitor.
                console.debug('AuthProvider: no current user', error);
                setStatus('anonymous');
            }
        })();
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const tokens = (await fetchAuthSession()).tokens;
                setIdToken(tokens?.idToken);
                setAccessToken(tokens?.accessToken);
                const groups = tokens?.idToken?.payload['cognito:groups'];
                if (Array.isArray(groups) && groups.length > 0) {
                    setCognitoGroups(groups as string[]);
                }
            } catch (error) {
                console.trace('AUTH CONTEXT. FETCH SESSION ERROR', error);
            }
        })();
    }, []);

    useEffect(() => {
        const groups = idToken?.payload['cognito:groups'];
        if (Array.isArray(groups) && groups.includes('admin')) {
            setRole('Admin');
        }
    }, [idToken]);

    return (
        <AuthContext.Provider value={{user, status, idToken, accessToken, cognitoGroups, role}}>
            {children}
        </AuthContext.Provider>
    );
};
